import {
  KycDocumentErrorCodes,
  KycDocumentErrorsType,
  KycDocumentNames,
} from 'commons/types';

/**
 * Industry rejections: the store's activity is not eligible, so no upload and
 * no re-analysis can change the outcome. Matched on the error code alone
 * because dlocal files them under `status_drop_down` and under `website` alike,
 * and the code is the part that carries the decision.
 */
const PROHIBITED_INDUSTRY_CODES = new Set<string>([
  KycDocumentErrorCodes.ValidationProhibitedIndustries,
  KycDocumentErrorCodes.ValidationLegalIssues,
  KycDocumentErrorCodes.WebsiteIndustryMismatch,
  KycDocumentErrorCodes.FinancialBusiness,
  KycDocumentErrorCodes.CarTruckServices,
  KycDocumentErrorCodes.AnimalProductsServices,
  KycDocumentErrorCodes.RealEstateServices,
  KycDocumentErrorCodes.RelationshipServices,
  KycDocumentErrorCodes.NonProfit,
  KycDocumentErrorCodes.Gambling,
  KycDocumentErrorCodes.MarketingPractices,
  KycDocumentErrorCodes.IllegalProductsServices,
  KycDocumentErrorCodes.MedicalProductsServices,
  KycDocumentErrorCodes.ArtAndJewellery,
  KycDocumentErrorCodes.HazardousMaterials,
  KycDocumentErrorCodes.TechnologyServices,
  KycDocumentErrorCodes.TurismServices,
  KycDocumentErrorCodes.SecuritiesAndTradingServices,
  KycDocumentErrorCodes.TicketAgencies,
  KycDocumentErrorCodes.AdultGoods,
  KycDocumentErrorCodes.ThirdPartyPaymentProcessing,
  KycDocumentErrorCodes.ExtractiveIndustry,
  KycDocumentErrorCodes.DropShipping,
  KycDocumentErrorCodes.GovernmentalServices,
  KycDocumentErrorCodes.PoliticalReligiousSocialCampaigning,
  KycDocumentErrorCodes.Rejected,
]);

/**
 * The tax id is rejected at the source (ARCA), so it is fixed in the tax
 * registry and not in Pago Nube. tax_id_expired and tax_id_other stay unmapped
 * on purpose: they carry no production volume yet, so they should keep
 * surfacing as kyc_unmapped_error rather than borrow this copy.
 */
const TAX_ID_CODES = new Set<string>([
  KycDocumentErrorCodes.TaxIdInvalid,
  KycDocumentErrorCodes.TaxIdInactive,
]);

/**
 * Codes the merchant fixes in their own store. Kept in step with
 * WEBSITE_REMEDIATION_TRANSLATIONS: each code here has its key there.
 */
const WEBSITE_REMEDIATION_CODES = new Set<string>([
  KycDocumentErrorCodes.WebsiteUnderConstruction,
  KycDocumentErrorCodes.WebsiteDeactivated,
  KycDocumentErrorCodes.WebsiteNotAvailable,
  KycDocumentErrorCodes.WebsiteTemplate,
  KycDocumentErrorCodes.WebsiteProductsMismatch,
  KycDocumentErrorCodes.WebsiteMissingPrices,
  KycDocumentErrorCodes.CounterfeitProducts,
]);

function hasKycErrors(kycDocumentsError: KycDocumentErrorsType[]) {
  const hasProofOfLifeError = kycDocumentsError.some(
    (error) => error.documentName === KycDocumentNames.ProofOfLife,
  );

  const isStatuteDocument = (error: KycDocumentErrorsType) =>
    error.documentName === KycDocumentNames.CompanyComposition ||
    error.documentName === KycDocumentNames.CertificateOfIncorporation;

  // The statute rejections only differ by error code, so matching on the
  // document name alone renders one "documento ilegible" for every defect.
  const hasStatuteErrorWithCode = (errorCode: KycDocumentErrorCodes) =>
    kycDocumentsError.some(
      (error) => isStatuteDocument(error) && error.errorCode === errorCode,
    );

  const hasStatuteError = kycDocumentsError.some(isStatuteDocument);

  const hasStatuteInvalidError = hasStatuteErrorWithCode(
    KycDocumentErrorCodes.DocumentsInvalid,
  );
  const hasStatuteMissingFieldsError = hasStatuteErrorWithCode(
    KycDocumentErrorCodes.DocumentsMissingFields,
  );
  const hasStatuteMissmatchError = hasStatuteErrorWithCode(
    KycDocumentErrorCodes.DocumentsMissmatch,
  );
  const hasStatuteMissingUboError = hasStatuteErrorWithCode(
    KycDocumentErrorCodes.ValidationMissingUbo,
  );
  const hasWebsiteError = kycDocumentsError.some(
    (error) =>
      error.errorCode.includes('website_') ||
      error.errorCode === KycDocumentErrorCodes.CounterfeitProducts,
  );

  const hasWebsiteUnderConstructionError = kycDocumentsError.some(
    (error) =>
      error.errorCode === KycDocumentErrorCodes.WebsiteUnderConstruction,
  );

  const hasDocumentsMissmatchError = kycDocumentsError.some(
    (error) => error.errorCode === KycDocumentErrorCodes.DocumentsMissmatch,
  );

  const hasWebsiteDeactivatedError = kycDocumentsError.some(
    (error) => error.errorCode === KycDocumentErrorCodes.WebsiteDeactivated,
  );
  const hasWebsiteNotAvailableError = kycDocumentsError.some(
    (error) => error.errorCode === KycDocumentErrorCodes.WebsiteNotAvailable,
  );
  const hasWebsiteTemplateError = kycDocumentsError.some(
    (error) => error.errorCode === KycDocumentErrorCodes.WebsiteTemplate,
  );
  const hasWebsiteProductsMismatchError = kycDocumentsError.some(
    (error) =>
      error.errorCode === KycDocumentErrorCodes.WebsiteProductsMismatch,
  );
  const hasCounterfeitProductsError = kycDocumentsError.some(
    (error) => error.errorCode === KycDocumentErrorCodes.CounterfeitProducts,
  );
  const hasWebsiteMissingPricesError = kycDocumentsError.some(
    (error) => error.errorCode === KycDocumentErrorCodes.WebsiteMissingPrices,
  );

  const hasWebsiteRemediationError = kycDocumentsError.some((error) =>
    WEBSITE_REMEDIATION_CODES.has(error.errorCode),
  );

  const hasDocumentsMissingFieldsError = kycDocumentsError.some(
    (error) => error.errorCode === KycDocumentErrorCodes.DocumentsMissingFields,
  );

  const hasUnderageError = kycDocumentsError.some(
    (error) => error.errorCode === KycDocumentErrorCodes.Underage,
  );

  const hasProhibitedIndustryError = kycDocumentsError.some((error) =>
    PROHIBITED_INDUSTRY_CODES.has(error.errorCode),
  );

  const hasTaxIdError = kycDocumentsError.some((error) =>
    TAX_ID_CODES.has(error.errorCode),
  );

  const hasLegalRepresentativeNotFoundInCompanyError = kycDocumentsError.some(
    (error) =>
      error.errorCode ===
      KycDocumentErrorCodes.LegalRepresentativeNotFoundInCompany,
  );

  const hasCopyOfIdFrontError = kycDocumentsError.some(
    (error) => error.documentName === KycDocumentNames.CopyOfIdFront,
  );

  const hasCopyOfIdBackError = kycDocumentsError.some(
    (error) => error.documentName === KycDocumentNames.CopyOfIdBack,
  );

  // Codes that carry no reason: dlocal itself does not say which defect it is,
  // so the generic message is the correct one. Matched explicitly so these stop
  // being reported as unmapped.
  const hasUnspecifiedError = kycDocumentsError.some(
    (error) =>
      error.errorCode === KycDocumentErrorCodes.Unknown ||
      error.errorCode === KycDocumentErrorCodes.DocumentsFailedOther ||
      error.errorCode === KycDocumentErrorCodes.ValidationFailedOther,
  );

  return {
    hasProofOfLifeError,
    hasStatuteError,
    hasStatuteInvalidError,
    hasStatuteMissingFieldsError,
    hasStatuteMissmatchError,
    hasStatuteMissingUboError,
    hasWebsiteUnderConstructionError,
    hasWebsiteError,
    hasDocumentsMissmatchError,
    hasWebsiteDeactivatedError,
    hasWebsiteNotAvailableError,
    hasWebsiteTemplateError,
    hasWebsiteProductsMismatchError,
    hasCounterfeitProductsError,
    hasWebsiteMissingPricesError,
    hasWebsiteRemediationError,
    hasDocumentsMissingFieldsError,
    hasUnderageError,
    hasProhibitedIndustryError,
    hasTaxIdError,
    hasLegalRepresentativeNotFoundInCompanyError,
    hasCopyOfIdFrontError,
    hasCopyOfIdBackError,
    hasUnspecifiedError,
  };
}

/**
 * Flags that drive the alert CTA navigation.
 *
 * The alert decides whether to navigate and `useIdValidationRoutes` decides
 * where to. Both derive from here so a new code cannot update only one half.
 * Every flag resolves to a route, so the alert must use
 * `shouldNavigateForPendingDoc` rather than re-listing them by hand.
 */
export type PendingDocNavigationFlags = {
  hasStatuteError: boolean;
  hasLegalRepresentativeNotFoundInCompanyError: boolean;
  hasProofOfLifeError: boolean;
  hasUnderageError: boolean;
  hasDocumentsMissingFieldsError: boolean;
  hasCopyOfIdFrontError: boolean;
  hasCopyOfIdBackError: boolean;
};

function getPendingDocNavigationFlags(
  kycDocumentsErrors: KycDocumentErrorsType[],
): PendingDocNavigationFlags {
  const {
    hasStatuteError,
    hasLegalRepresentativeNotFoundInCompanyError,
    hasProofOfLifeError,
    hasUnderageError,
    hasDocumentsMissingFieldsError,
    hasCopyOfIdFrontError,
    hasCopyOfIdBackError,
  } = hasKycErrors(kycDocumentsErrors);

  return {
    hasStatuteError,
    hasLegalRepresentativeNotFoundInCompanyError,
    hasProofOfLifeError,
    hasUnderageError,
    hasDocumentsMissingFieldsError,
    hasCopyOfIdFrontError,
    hasCopyOfIdBackError,
  };
}

/**
 * True when any navigation flag is set. Reads the whole object on purpose: a
 * new flag counts as navigable without touching this function.
 */
function shouldNavigateForPendingDoc(
  flags: PendingDocNavigationFlags,
): boolean {
  return Object.values(flags).some(Boolean);
}

function getKycErrorsTranslation(kycDocumentsErrors: KycDocumentErrorsType[]) {
  const {
    hasStatuteError,
    hasStatuteInvalidError,
    hasStatuteMissingFieldsError,
    hasStatuteMissmatchError,
    hasStatuteMissingUboError,
    hasProofOfLifeError,
    hasWebsiteUnderConstructionError,
    hasDocumentsMissmatchError,
    hasWebsiteDeactivatedError,
    hasWebsiteNotAvailableError,
    hasWebsiteTemplateError,
    hasWebsiteProductsMismatchError,
    hasCounterfeitProductsError,
    hasWebsiteMissingPricesError,
    hasWebsiteRemediationError,
    hasDocumentsMissingFieldsError,
    hasUnderageError,
    hasProhibitedIndustryError,
    hasTaxIdError,
    hasLegalRepresentativeNotFoundInCompanyError,
    hasCopyOfIdFrontError,
    hasCopyOfIdBackError,
    hasUnspecifiedError,
  } = hasKycErrors(kycDocumentsErrors);

  // Non-remediable rejections first: re-uploading cannot fix them, so a
  // remediable error sharing the set must never mask them. Within the block the
  // order is how final the rejection is: an ineligible industry or a rejected
  // tax id closes the account for good, while the rest at least tell the
  // merchant which document was wrong.
  if (hasProhibitedIndustryError) return 'prohibitedIndustryError';
  if (hasTaxIdError) return 'taxIdError';
  if (hasUnderageError) return 'underageError';
  if (hasStatuteMissmatchError) return 'statuteMissmatchError';
  if (hasDocumentsMissmatchError) return 'documentsMissmatchError';

  // A pending proof of life keeps the KYC case blocked in the payment gateway,
  // so the website is not re-sent to dlocal until the selfie is done: a website
  // message alone would have the merchant re-checking a store that can never
  // clear. The message names both steps and the CTA routes to the selfie.
  // With a statute error the statute variant wins even when its code is a
  // website one: useIdValidationRoutes sends any statute error to the statute
  // step first, so the message has to match where the CTA lands.
  if (hasProofOfLifeError && hasWebsiteRemediationError)
    return hasStatuteError
      ? 'statuteAndProofOfLifeError'
      : 'websiteAndProofOfLifeError';

  // website_under_construction leads the remediable errors: it is verified by a
  // refetch instead of a new analyst cycle, so it is the cheapest step for the
  // merchant, and whatever else is in the set surfaces once it clears. dlocal
  // also files this code under copy_of_id* and under the statute, so the error
  // code wins over the document it was stapled to.
  if (hasWebsiteUnderConstructionError) return 'websiteUnderConstructionError';

  // Statute branches, most specific first.
  if (hasStatuteError && hasProofOfLifeError)
    return 'statuteAndProofOfLifeError';
  if (hasStatuteInvalidError) return 'statuteInvalidError';
  if (hasStatuteMissingFieldsError) return 'statuteMissingFieldsError';
  if (hasStatuteMissingUboError) return 'statuteMissingUboError';
  if (hasStatuteError) return 'statuteError';

  if (hasWebsiteDeactivatedError) return 'websiteDeactivatedError';
  if (hasWebsiteNotAvailableError) return 'websiteNotAvailableError';
  if (hasWebsiteTemplateError) return 'websiteTemplateError';
  if (hasWebsiteProductsMismatchError) return 'websiteProductsMismatchError';
  if (hasCounterfeitProductsError) return 'counterfeitProductsError';
  if (hasWebsiteMissingPricesError) return 'websiteMissingPricesError';
  if (hasDocumentsMissingFieldsError) return 'documentsMissingFieldsError';
  if (hasLegalRepresentativeNotFoundInCompanyError)
    return 'legalRepresentativeNotFoundInCompanyWithAdditionalDocError';
  if (hasCopyOfIdFrontError) return 'copyOfIdFrontError';
  if (hasCopyOfIdBackError) return 'copyOfIdBackError';
  if (hasProofOfLifeError) return 'proofOfLifeError';
  if (hasUnspecifiedError) return 'generalError';
  return '';
}

/**
 * Messages that ask the merchant to change something in their own store rather
 * than upload a document. Their CTA re-checks the validation, because there is
 * no screen in this app that fixes them and sending the merchant to an upload
 * they were never told about is the mismatch this list exists to prevent.
 */
const WEBSITE_REMEDIATION_TRANSLATIONS = new Set<string>([
  'websiteUnderConstructionError',
  'websiteDeactivatedError',
  'websiteNotAvailableError',
  'websiteTemplateError',
  'websiteProductsMismatchError',
  'websiteMissingPricesError',
  'counterfeitProductsError',
]);

/**
 * Messages the merchant cannot act on at all. The alert renders these without a
 * CTA: every button it could offer would restart a validation that can only end
 * in the same rejection.
 */
const NON_REMEDIABLE_TRANSLATIONS = new Set<string>([
  'prohibitedIndustryError',
  'taxIdError',
]);

/**
 * Messages whose reason lives outside Pago Nube, so the alert points at the
 * help article that lists it instead of at a screen in this app.
 */
const HELP_LINK_TRANSLATIONS = new Set<string>(['prohibitedIndustryError']);

function hasKycErrorHelpLink(translationKey: string): boolean {
  return HELP_LINK_TRANSLATIONS.has(translationKey);
}

function remediatesWebsite(translationKey: string): boolean {
  return WEBSITE_REMEDIATION_TRANSLATIONS.has(translationKey);
}

function isNonRemediableKycError(translationKey: string): boolean {
  return NON_REMEDIABLE_TRANSLATIONS.has(translationKey);
}

const PROOF_OF_LIFE_DEFAULT_TRANSLATION = 'proofOfLifeError';

const PROOF_OF_LIFE_ERROR_TRANSLATION_BY_CODE: Record<string, string> = {
  [KycDocumentErrorCodes.DocumentsMissmatch]: 'documentsMissmatchError',
};

function getProofOfLifeErrorTranslation(errorCode?: string): {
  translationKey: string;
  isMapped: boolean;
} {
  const mappedTranslation = errorCode
    ? PROOF_OF_LIFE_ERROR_TRANSLATION_BY_CODE[errorCode]
    : undefined;

  return {
    translationKey: mappedTranslation ?? PROOF_OF_LIFE_DEFAULT_TRANSLATION,
    isMapped: Boolean(mappedTranslation),
  };
}

export {
  hasKycErrors,
  getPendingDocNavigationFlags,
  shouldNavigateForPendingDoc,
  getKycErrorsTranslation,
  getProofOfLifeErrorTranslation,
  remediatesWebsite,
  isNonRemediableKycError,
  hasKycErrorHelpLink,
};
